import type { ReactNode } from "react";
import { useEffect } from "react";
import { Button, ButtonGroup, Col, Container, Form, Row, Spinner, ToggleButton } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import BgWaveTop from "images/bg/bckg-branded-top.svg";
import OneThumb from "images/icons/1palec.svg";
import TwoThumbs from "images/icons/2palce.svg";
import ThreeThumbs from "images/icons/3palce.svg";

import { useAppSelector } from "store/hooks";
import type { TAssignmentStatus } from "store/slices/exercise";
import { getTotalGainedPoints, getTotalMaxPoints } from "store/slices/exercise";
import { getGlobalTime } from "store/slices/timer";

import { MOOD_ICONS } from "const";
import { gtmExerciseFinished } from "utils/gtmEvents";

const calculateNumberOfFinishedAssignments = <T extends { status: TAssignmentStatus }>(results: Record<string, T>) => {
  const entries = Object.values(results);
  const total = entries.length;
  const finished = entries.filter(assignment => assignment.status !== "not_taken").length;
  const correct = entries.filter(assignment => assignment.status === "correct").length;

  return {
    total,
    finished,
    correct,
  };
};

interface Props<T> {
  mood: number;
  moodText: string;
  selfReflectionText: string;
  results: Record<string, T>;
  onMoodSelect: (value: number) => void;
  onMoodTextChange: (text: string) => void;
  onSelfReflectionTextChange: (text: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  hasSubmitFailed: boolean;
  children?: ReactNode;
}

const ExerciseEvaluation = <T extends { status: TAssignmentStatus; points?: number }>({
  mood,
  moodText,
  selfReflectionText,
  results,
  onMoodSelect,
  onMoodTextChange,
  onSelfReflectionTextChange,
  onSubmit,
  isSubmitting,
  hasSubmitFailed,
}: Props<T>) => {
  const globalTimeInSeconds = useAppSelector(getGlobalTime);
  const totalGainedPoints = useAppSelector(getTotalGainedPoints);
  const totalMaxPoints = useAppSelector(getTotalMaxPoints);
  const successRate = totalGainedPoints / totalMaxPoints;
  const finishedAssignments = calculateNumberOfFinishedAssignments(results);
  const timeInMinutesSpent = Math.round(globalTimeInSeconds / 60);

  const { t } = useTranslation(["exercise"]);

  useEffect(() => {
    gtmExerciseFinished(
      finishedAssignments.total,
      finishedAssignments.finished,
      finishedAssignments.correct,
      timeInMinutesSpent,
    );
  }, []);

  const getTitle = () => {
    if (successRate > 0.75) {
      return t("header.evaluation-3", { ns: "exercise" });
    }

    if (successRate > 0.5) {
      return t("header.evaluation-2", { ns: "exercise" });
    }

    return t("header.evaluation-1", { ns: "exercise" });
  };

  const getTitleIcon = () => {
    if (successRate > 0.75) {
      return <ThreeThumbs />;
    }

    if (successRate > 0.5) {
      return <TwoThumbs />;
    }

    return <OneThumb />;
  };

  const getSuccessRateText = () => {
    if (successRate >= 0.9) {
      return "90-100 %";
    }
    if (successRate >= 0.6) {
      return "60-89 %";
    }

    return "0-59 %";
  };
  return (
    <main className={"fullpage fullpage--split"}>
      <BgWaveTop className={"wave__top wave__top--pink"} />
      <div className="fullpage__content fullpage__content--thin bg-cyan-200">
        <Container className="container-mw-dm py-3 text-center justify-content-center">
          <div className="page">
            <div className="page__content page__content--spaced my-auto ms-sm-0 pt-50px d-flex flex-row align-items-center justify-content-center">
              <div className="text-center">{getTitleIcon()}</div>
              <div className="w-330">
                <h1 className="h4 mb-20px text-start">{getTitle()}</h1>

                <div className="practice-evaluation__summary">
                  <p className={"d-flex flex-column mb-0"}>
                    <span className={"mb-8px"}>{t("label.questions", { ns: "exercise" })}</span>
                    <strong>{getSuccessRateText()}</strong>
                  </p>
                  <p className={"d-flex flex-column mb-0"}>
                    <span className={"mb-8px"}>{t("label.time", { ns: "exercise" })}</span>
                    <strong>{`${timeInMinutesSpent} min`}</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <div className="fullpage__footer">
        <Container className="container-mw-sm">
          <Row className={"mb-20px"}>
            <Col className={"text-center"}>
              <h3 className={"mb-20px"} id="after-practice">
                {t("header.afterPractice", { ns: "exercise" })}
              </h3>
              <ButtonGroup className={"btn-group-emoji mb-20px"}>
                {MOOD_ICONS.map((radio, idx) => (
                  <ToggleButton
                    key={idx}
                    id={`radio-1-${idx}`}
                    type="radio"
                    variant="select"
                    name="radio1"
                    value={radio.value}
                    checked={mood === radio.value}
                    onChange={e => {
                      const value = Number(e.currentTarget.value);
                      if (!isNaN(value)) {
                        onMoodSelect(value);
                      }
                    }}
                  >
                    {radio.name}
                  </ToggleButton>
                ))}
              </ButtonGroup>
              {mood !== null ? (
                <>
                  <p className="text-center">
                    <strong>Za co se pochválíš</strong>
                  </p>
                  <Form.Control
                    as={"textarea"}
                    placeholder={t("info.moodPlaceholder", { ns: "exercise" })}
                    onChange={e => {
                      onMoodTextChange(e.target.value);
                    }}
                    value={moodText}
                  />

                  <p className="text-center mt-3">
                    <strong>Předsevzetí pro příště</strong>
                  </p>
                  <Form.Control
                    as={"textarea"}
                    placeholder={t("info.selfReflectionPlaceholder", { ns: "exercise" })}
                    onChange={e => {
                      onSelfReflectionTextChange(e.target.value);
                    }}
                    value={selfReflectionText}
                  />
                </>
              ) : null}
            </Col>
          </Row>

          {hasSubmitFailed ? (
            <p className="text-danger text-center" role="alert">
              {t("info.closeFailed", { ns: "exercise" })}
            </p>
          ) : null}

          {/* Submit button */}
          <Button className={"text-uppercase w-100"} disabled={mood === 0 || isSubmitting} onClick={onSubmit}>
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" aria-hidden="true" className={"me-2"} />
                {t("btn.savingResults", { ns: "exercise" })}
              </>
            ) : (
              t("btn.showResults", { ns: "exercise" })
            )}
          </Button>
        </Container>
      </div>
    </main>
  );
};

export default ExerciseEvaluation;
